import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import { emiOptions } from "../../constants";
import { useProject } from "../../hooks";
import { formatDate } from "../../services/utils";
import { Button, SubmitButton } from "../buttons/Buttons";
import FormWrapper from "../forms/Form";
import LinkUploadModal from "../modals/LinkUploadModal";

function EditProjectForm({ data, onClose }) {
  const [projectData, setProjectData] = useState(data);
  const [editedData, setEditedData] = useState({});
  const [showLinkUploadModal, setShowLinkUploadModal] = useState(false);
  const { updateProjectById } = useProject();

  const handleInputChange = (e, useTextCase) => {
    const { name, value } = e.target;
    let newValue = value;
    if (useTextCase === "capitalize")
      newValue = value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1));
    if (useTextCase === "capitalize_first") newValue = value.charAt(0).toUpperCase() + value.slice(1);
    if (useTextCase === "uppercase") newValue = value.toUpperCase();

    setProjectData((prevData) => ({ ...prevData, [name]: newValue }));
    setEditedData((prevData) => ({ ...prevData, [name]: newValue }));
    const savedData = JSON.parse(localStorage.getItem("edit_project_data"));
    localStorage.setItem("edit_project_data", JSON.stringify({ ...savedData, [name]: newValue }));
  };

  const handleSaveLink = (link) => {
    setShowLinkUploadModal(false);
    if (link === projectData.google_maps_url) return;
    setProjectData((prevData) => ({ ...prevData, google_maps_url: link }));
    setEditedData((prevData) => ({ ...prevData, google_maps_url: link }));
    const savedData = JSON.parse(localStorage.getItem("edit_project_data"));
    localStorage.setItem("edit_project_data", JSON.stringify({ ...savedData, google_maps_url: link }));
  };

  const handleClose = () => {
    localStorage.removeItem("edit_project_data");
    onClose(false);
  };

  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem("edit_project_data"));
    if (savedData) setProjectData((prevData) => ({ ...prevData, ...savedData }));
  }, []);

  const handleEdit = async (e) => {
    e.preventDefault();
    // check if editedData is {}
    if (Object.keys(editedData).length === 0) return onClose(false);
    const savedData = JSON.parse(localStorage.getItem("edit_project_data"));
    if (savedData && savedData.completion_eta) {
      savedData.completion_eta = formatDate(savedData.completion_eta);
    }
    const dataToSubmit = { ...editedData };
    if (dataToSubmit.completion_eta) {
      dataToSubmit.completion_eta = formatDate(dataToSubmit.completion_eta);
    }
    if (dataToSubmit.emi_options) dataToSubmit.emi_options = `${dataToSubmit.emi_options}`;
    if (dataToSubmit.plot_area_min) dataToSubmit.plot_area_min = +dataToSubmit.plot_area_min;
    if (dataToSubmit.plot_area_max) dataToSubmit.plot_area_max = +dataToSubmit.plot_area_max;
    if (dataToSubmit.days_of_grace) dataToSubmit.days_of_grace = +dataToSubmit.days_of_grace;
    const { success } = await updateProjectById(data.id, dataToSubmit);
    if (!success) return toast.error("Error while updating project");
    toast.success("Project updated successfully");
    localStorage.removeItem("edit_projecst_data");
    onClose(true);
  };

  return (
    <>
      <FormWrapper id="edit_project_form" onSubmit={handleEdit}>
        <div className="section grid_3">
          <div className="column">
            <input
              name="company_name"
              type="text"
              className="input"
              value={projectData.company_name}
              onChange={(e) => handleInputChange(e, "capitalize")}
              placeholder="Enter Company Name"
              required
              pattern="([a-zA-Z0-9\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              name="state"
              type="text"
              className="input"
              value={projectData.state}
              onChange={(e) => handleInputChange(e, "capitalize")}
              placeholder="Enter State"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              name="city"
              type="text"
              className="input"
              value={projectData.city}
              onChange={(e) => handleInputChange(e, "capitalize")}
              placeholder="Enter City"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              name="gaon"
              type="text"
              className="input"
              value={projectData.gaon}
              onChange={(e) => handleInputChange(e, "capitalize")}
              placeholder="Enter Gaon"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <input
              name="taluka"
              type="text"
              className="input"
              value={projectData.taluka}
              onChange={(e) => handleInputChange(e, "capitalize")}
              placeholder="Enter Taluka"
              required
              pattern="([a-zA-Z\s+]+)"
              title="Only alphabets are allowed"
              maxLength={50}
            />
            <div className="grid_3">
              <div className="input_with_suffix_wrapper">
                <input
                  type="number"
                  name="plot_area_min"
                  required
                  onChange={handleInputChange}
                  value={projectData.plot_area_min}
                  className="input"
                  min={1}
                  max={projectData.plot_area_max}
                  placeholder="Min Area"
                />
                <span className="suffix">Sq. Ft.</span>
              </div>
              <span className="is-center label">Plot Area</span>
              <div className="input_with_suffix_wrapper">
                <input
                  type="number"
                  name="plot_area_max"
                  required
                  onChange={handleInputChange}
                  value={projectData.plot_area_max}
                  className="input"
                  min={projectData.plot_area_min}
                  placeholder="Max Area"
                />
                <span className="suffix">Sq. Ft.</span>
              </div>
            </div>
            <input
              name="short_location"
              type="text"
              className="input"
              value={projectData.short_location}
              onChange={(e) => handleInputChange(e, "capitalize_first")}
              placeholder="Enter Short Location"
              required
            />
            <input
              name="zone"
              type="text"
              className="input"
              value={projectData.zone}
              onChange={(e) => handleInputChange(e, "capitalize_first")}
              placeholder="Enter Zone"
              required
            />
            <input
              name="road_to_plot"
              type="text"
              className="input"
              value={projectData.road_to_plot}
              onChange={(e) => handleInputChange(e, "capitalize_first")}
              placeholder="Enter Road to Plot"
              required
            />
            <div className="row">
              <span className="label">Google maps location</span>
              <Link to={projectData.google_maps_url} target="_blank" rel="noopener noreferrer" className="preview">
                open link
              </Link>
              <Button className="ghost_outline_normal" onClick={() => setShowLinkUploadModal(true)}>
                upload link
              </Button>
            </div>
          </div>
          <div className="vertical_divider"></div>
          <div className="column spread">
            <div className="row">
              <span className="label">Sanctioning Authority</span>
              <input
                name="sanctioning_authority"
                type="text"
                className="input full"
                value={projectData.sanctioning_authority}
                onChange={(e) => handleInputChange(e, "uppercase")}
                placeholder="Enter Sanctioning Authority"
                required
              />
            </div>
            <div className="row">
              <span className="label">Type</span>
              <div className="row">
                <label htmlFor="proposed" className="label radio_label">
                  <input
                    type="radio"
                    name="project_type"
                    id="proposed"
                    value="Proposed NA"
                    checked={projectData.project_type?.toLowerCase() === "proposed na"}
                    onChange={handleInputChange}
                    required
                  />
                  Proposed NA
                </label>
                <label htmlFor="na" className="label radio_label">
                  <input
                    type="radio"
                    name="project_type"
                    id="na"
                    value="NA"
                    onChange={handleInputChange}
                    checked={projectData.project_type?.toLowerCase() === "na"}
                  />
                  NA
                </label>
                <label htmlFor="r_zone" className="label radio_label">
                  <input
                    type="radio"
                    name="project_type"
                    id="r_zone"
                    value="R Zone"
                    onChange={handleInputChange}
                    checked={projectData.project_type?.toLowerCase() === "r zone"}
                  />
                  R Zone
                </label>
              </div>
            </div>
            <div className="grid_3">
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="start_range"
                  required
                  className="input"
                  onChange={handleInputChange}
                  value={projectData.start_range}
                  placeholder="Start Range"
                  max={projectData.end_range}
                  min={0}
                />
              </div>
              <span className="label is-center">Rate Range</span>
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="end_range"
                  required
                  onChange={handleInputChange}
                  value={projectData.end_range}
                  className="input"
                  placeholder="End Range"
                  min={projectData.start_range}
                />
              </div>
            </div>
            <div className="row">
              <span className="label">EMI Options</span>
              <Select
                name="emi_options"
                className="select"
                value={emiOptions.find((option) => option.value === `${projectData.emi_options}`)}
                onChange={(value) => handleInputChange({ target: { name: "emi_options", value: value.value } })}
                required
                options={emiOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: "1px solid var(--gray-light)",
                    boxShadow: "none",
                    ":hover": {
                      border: "1px solid var(--gray-light)",
                    },
                  }),
                }}
              />
            </div>
            <div className="row">
              <span className="label">Token Percent</span>
              <div className="input_with_suffix_wrapper">
                <input
                  type="number"
                  name="token_percent"
                  className="input"
                  value={projectData.token_percent}
                  onChange={handleInputChange}
                  placeholder="Enter Token Percent"
                  required
                  min={0}
                  max={100}
                />
                <span className="suffix">%</span>
              </div>
            </div>
            <div className="row">
              <span className="label">Maha Rera Number</span>
              <input
                type="text"
                name="rera_number"
                className="input"
                value={projectData.rera_number}
                onChange={handleInputChange}
                placeholder="Enter Maha Rera Number"
                required
              />
            </div>
            <div className="row">
              <span className="label">
                Development Charges <br />
                (Water & Electricity)
              </span>
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="development_charges"
                  className="input"
                  value={projectData.development_charges}
                  onChange={handleInputChange}
                  placeholder="Enter Development Charges"
                  required
                  min={0}
                />
              </div>
            </div>
            <div className="row">
              <span className="label">
                Registration Charges <br />+ Advocate Fees
              </span>
              <div className="input_with_prefix_wrapper">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  name="registration_charges"
                  className="input"
                  value={projectData.registration_charges}
                  onChange={handleInputChange}
                  placeholder="Enter Registration Charges"
                  required
                  min={0}
                />
              </div>
            </div>
            <div className="row">
              <span className="label">Project Completion ETA</span>
              <input
                type="date"
                name="completion_eta"
                className="input"
                value={projectData.completion_eta.split("/").reverse().join("-")}
                onChange={handleInputChange}
                placeholder="Enter Project Completion ETA"
                required
              />
            </div>
            <div className="row">
              <span className="label">Limit number of plots agent can put on priority</span>
              <input
                type="number"
                name="plot_hold_limit"
                required
                className="input"
                placeholder="Enter no."
                value={projectData.plot_hold_limit}
                onChange={handleInputChange}
                min={0}
              />
            </div>
          </div>
        </div>
        <div className="row end">
          <Button className="ghost_gray" onClick={handleClose}>
            Cancel
          </Button>
          <SubmitButton className="submit" form="edit_project_form">
            Save
          </SubmitButton>
        </div>
      </FormWrapper>
      {showLinkUploadModal &&
        createPortal(
          <LinkUploadModal
            handleClose={() => setShowLinkUploadModal(false)}
            isVisible={showLinkUploadModal}
            handleSave={handleSaveLink}
            data={projectData.google_maps_url}
            linkType="google_maps_url"
            invalidMessage="Please enter a valid google maps url"
          />,
          document.body
        )}
    </>
  );
}

export default EditProjectForm;
